import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useUser } from "./UserContext";

const AdminDashboardContext = createContext(null);

/* ================= API base (same convention as LessonsContext) ================= */
const ENV_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;

const DEFAULT_TIMEOUT_MS = 12000;
const url = (path) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

/* ================= tiny helpers (mirrors LessonsContext) ================= */
async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function apiGet(path, { timeout = DEFAULT_TIMEOUT_MS, headers = {} } = {}) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url(path), {
      method: "GET",
      mode: "cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
      headers,
    });
    const data = await parseJSONSafely(res);
    if (!res.ok) {
      const msg =
        data?.message ||
        data?.error ||
        (res.status === 401
          ? "שגיאה בהרשאות (401)"
          : res.status === 403
          ? "אין הרשאת מנהל לצפייה בנתונים אלו (403)"
          : `שגיאת שרת (${res.status})`);
      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    return data ?? {};
  } catch (e) {
    if (e.name === "AbortError") throw new Error("זמן ההמתנה לשרת הסתיים. נסה שוב בעוד רגע.");
    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב- ${API_BASE} וש-CORS מאפשר את המקור של הלקוח.`
        : "הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.";
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

function getBearer() {
  try {
    if (typeof window === "undefined") return null;
    const keys = ["token", "jwt", "JWT", "accessToken", "authToken"];
    for (const k of keys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
  } catch {
    // ignore
  }
  return null;
}

async function apiGetAuth(path, opts = {}) {
  const token = getBearer();
  if (!token) {
    const err = new Error("Missing bearer token");
    err.status = 401;
    throw err;
  }
  return apiGet(path, {
    ...(opts || {}),
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(opts.headers || {}),
    },
  });
}

const toQuery = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) q.set(k, String(v));
  });
  return q.toString();
};

/* ================= endpoint calls =================
   Paths match routes.php: /api/admin/dashboard/*
   Response unwrapping matches AdminDashboardController.php:
   list endpoints -> {ok, items}, stat endpoints -> {ok, stats},
   summary/trends/bookings -> flat top-level keys. */

const DASHBOARD_BASE = "/api/admin/dashboard";

const fetchDashboardSummary  = (params) => apiGetAuth(`${DASHBOARD_BASE}/summary?${toQuery(params)}`);
const fetchDashboardTrends   = (params) => apiGetAuth(`${DASHBOARD_BASE}/trends?${toQuery(params)}`);
const fetchBookingStatistics = (params) => apiGetAuth(`${DASHBOARD_BASE}/bookings?${toQuery(params)}`);

const fetchTopTeachers = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/teachers/top?${toQuery(params)}`);
  return Array.isArray(data?.items) ? data.items : [];
};
const fetchTopSubjects = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/subjects/top?${toQuery(params)}`);
  return Array.isArray(data?.items) ? data.items : [];
};
const fetchRecentActivities = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/activities?${toQuery(params)}`);
  return Array.isArray(data?.items) ? data.items : [];
};
const fetchSystemAlerts = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/alerts?${toQuery(params)}`);
  return Array.isArray(data?.items) ? data.items : [];
};
const fetchStudentsStatistics = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/students?${toQuery(params)}`);
  return data?.stats || null;
};
const fetchMeetingsStatistics = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/meetings?${toQuery(params)}`);
  return data?.stats || null;
};
const fetchAutomaticInsights = async (params) => {
  const data = await apiGetAuth(`${DASHBOARD_BASE}/insights?${toQuery(params)}`);
  return Array.isArray(data?.items) ? data.items : [];
};

/* ================= generic data-slice hook =================
   One fetch/loading/error/retry slot per dashboard section. `enabled`
   gates the fetch on isAdmin so non-admins never hit these endpoints. */
function useDashboardSlice(enabled, fetcher, depsKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, depsKey, tick]);

  const retry = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, retry };
}

/* ================= filters ================= */
export const EMPTY_ADMIN_DASHBOARD_FILTERS = {
  subject: "",
  teacherId: "",
  lessonType: "",
  lessonStatus: "",
  meetingType: "",
};

/* ====================== Provider ====================== */
export const AdminDashboardProvider = ({ children }) => {
  const { isLogedin, role, user } = useUser();

  const isAdmin = !!isLogedin && (role === "admin" || user?.role === "admin");

  const [dateRange, setDateRange] = useState({ preset: "30d", from: null, to: null });
  const [filters, setFilters] = useState(EMPTY_ADMIN_DASHBOARD_FILTERS);
  const [granularity, setGranularity] = useState("month");
  const [refreshTick, setRefreshTick] = useState(0);

  const baseParams = useMemo(
    () => ({
      preset: dateRange.preset,
      from: dateRange.from || "",
      to: dateRange.to || "",
      subject: filters.subject,
      teacherId: filters.teacherId,
      lessonType: filters.lessonType,
      lessonStatus: filters.lessonStatus,
      meetingType: filters.meetingType,
    }),
    [dateRange, filters]
  );

  const trendsParams = useMemo(
    () => ({ ...baseParams, granularity }),
    [baseParams, granularity]
  );

  const baseKey = useMemo(() => JSON.stringify(baseParams), [baseParams]);
  const trendsKey = useMemo(() => JSON.stringify(trendsParams), [trendsParams]);

  const summary = useDashboardSlice(
    isAdmin,
    () => fetchDashboardSummary(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const trends = useDashboardSlice(
    isAdmin,
    () => fetchDashboardTrends(trendsParams),
    `${trendsKey}|${refreshTick}`
  );
  const booking = useDashboardSlice(
    isAdmin,
    () => fetchBookingStatistics(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const topTeachers = useDashboardSlice(
    isAdmin,
    () => fetchTopTeachers(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const topSubjects = useDashboardSlice(
    isAdmin,
    () => fetchTopSubjects(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const activities = useDashboardSlice(
    isAdmin,
    () => fetchRecentActivities(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const alertsSlice = useDashboardSlice(
    isAdmin,
    () => fetchSystemAlerts(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const studentsStats = useDashboardSlice(
    isAdmin,
    () => fetchStudentsStatistics(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const meetingsStats = useDashboardSlice(
    isAdmin,
    () => fetchMeetingsStatistics(baseParams),
    `${baseKey}|${refreshTick}`
  );
  const insights = useDashboardSlice(
    isAdmin,
    () => fetchAutomaticInsights(baseParams),
    `${baseKey}|${refreshTick}`
  );

  /* local "mark as resolved" for alerts — mirrors markLessonsPurchased's
     pattern of an optimistic local patch; wire to a real PATCH endpoint
     later if you want resolution to persist server-side. */
  const [resolvedAlertIds, setResolvedAlertIds] = useState([]);
  const resolveAlert = useCallback((alertId) => {
    setResolvedAlertIds((prev) =>
      prev.some((id) => String(id) === String(alertId)) ? prev : [...prev, alertId]
    );
  }, []);
  const visibleAlerts = useMemo(() => {
    if (!alertsSlice.data) return alertsSlice.data;
    return alertsSlice.data.filter(
      (a) => !resolvedAlertIds.some((id) => String(id) === String(a.id))
    );
  }, [alertsSlice.data, resolvedAlertIds]);

  useEffect(() => {
    setResolvedAlertIds([]);
  }, [baseKey]);

  const refreshAll = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  const isAnyLoading =
    summary.loading ||
    trends.loading ||
    booking.loading ||
    topTeachers.loading ||
    topSubjects.loading ||
    activities.loading ||
    alertsSlice.loading ||
    studentsStats.loading ||
    meetingsStats.loading ||
    insights.loading;

  const subjectOptions = useMemo(
    () => (topSubjects.data || []).map((s) => ({ value: s.subject, label: s.subject })),
    [topSubjects.data]
  );
  const teacherOptions = useMemo(
    () => (topTeachers.data || []).map((t) => ({ value: String(t.id), label: t.name })),
    [topTeachers.data]
  );

  const exportReport = useCallback((format) => {
    // REPLACE: no export endpoint exists yet — wire this to a real one, e.g.
    // window.open(url(`${DASHBOARD_BASE}/export.${format}?${toQuery(baseParams)}`), '_blank')
    console.log("export requested:", format, baseParams);
  }, [baseParams]);

  const value = useMemo(
    () => ({
      isAdmin,

      // filters
      dateRange,
      setDateRange,
      filters,
      setFilters,
      granularity,
      setGranularity,
      subjectOptions,
      teacherOptions,

      // data slices
      summary,
      trends,
      booking,
      topTeachers,
      topSubjects,
      activities,
      alerts: { ...alertsSlice, data: visibleAlerts },
      studentsStats,
      meetingsStats,
      insights,

      // actions
      resolveAlert,
      refreshAll,
      exportReport,
      isAnyLoading,
      lastUpdated: summary.data?.lastUpdated || null,
    }),
    [
      isAdmin,
      dateRange,
      filters,
      granularity,
      subjectOptions,
      teacherOptions,
      summary,
      trends,
      booking,
      topTeachers,
      topSubjects,
      activities,
      alertsSlice,
      visibleAlerts,
      studentsStats,
      meetingsStats,
      insights,
      resolveAlert,
      refreshAll,
      exportReport,
      isAnyLoading,
    ]
  );

  if (typeof window !== "undefined" && !CLEAN_ENV_BASE) {
    console.warn(
      `[AdminDashboardContext] API base not set in env. Falling back to ${FALLBACK_BASE}. Set VITE_API_BASE / REACT_APP_API_BASE for development.`
    );
  }

  return (
    <AdminDashboardContext.Provider value={value}>
      {children}
    </AdminDashboardContext.Provider>
  );
};

export const useAdminDashboard = () => {
  const ctx = useContext(AdminDashboardContext);
  if (!ctx) throw new Error("useAdminDashboard must be used within an AdminDashboardProvider");
  return ctx;
};